import { useFeedback } from "@taunggyi-plus/auth";
import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { Card } from "@taunggyi-plus/design-system";
type Org = { id: string; name: string };
type Availability = {
  organization_id: string;
  paused: boolean;
  opens_at: string;
  closes_at: string;
  weekdays: number[];
  note: string;
};
type Subscription = {
  organization_id: string;
  status: string;
  renews_at: string | null;
  grace_days: number;
};
export function ProviderSettings({
  admin = false,
  ownerRole,
}: {
  admin?: boolean;
  ownerRole?: string;
}) {
  const { client, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [now] = useState(() => Date.now());
  const [orgs, setOrgs] = useState<Org[]>([]),
    [availability, setAvailability] = useState<Availability[]>([]),
    [subscriptions, setSubscriptions] = useState<Subscription[]>([]),
    [org, setOrg] = useState(""),
    [message, setMessage] = useFeedback(),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!client || !user) return;
    let active = true;
    void Promise.all([
      client.from("organizations").select("id,name"),
      admin
        ? Promise.resolve({ data: [], error: null })
        : client
            .from("organization_memberships")
            .select("organization_id,role")
            .eq("user_id", user.id)
            .eq("status", "active"),
      client.from("provider_availability").select("*"),
      client
        .from("organization_subscriptions")
        .select("organization_id,status,renews_at,grace_days"),
    ])
      .then(([o, memberships, a, s]) => {
        if (!active) return;
        const err = [o, memberships, a, s].find((r) => r.error)?.error;
        if (err) {
          setMessage(err.message);
          return;
        }
        const ownedIds = new Set(
          ((memberships.data ?? []) as { organization_id: string; role: string }[])
            .filter((m) => !ownerRole || m.role === ownerRole)
            .map((m) => m.organization_id),
        );
        const accessible = ((o.data ?? []) as Org[]).filter((o) => admin || ownedIds.has(o.id));
        setOrgs(accessible);
        if (!admin)
          setOrg((current) =>
            accessible.some((o) => o.id === current) ? current : (accessible[0]?.id ?? ""),
          );
        setAvailability((a.data ?? []) as Availability[]);
        setSubscriptions((s.data ?? []) as Subscription[]);
      })
      .catch(() => {
        if (active) setMessage("Could not load provider settings.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [client, user, admin, ownerRole, setMessage]);
  const value = availability.find((a) => a.organization_id === org),
    sub = subscriptions.find((s) => s.organization_id === org);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!client || busy) return;
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      const r = await client.rpc("set_provider_availability", {
        org,
        paused: f.has("paused"),
        opens: f.get("opens"),
        closes: f.get("closes"),
        days: f.getAll("days").map(Number),
        note: f.get("note"),
      });
      if (r.error) throw new Error(r.error.message);
      setAvailability((current) => [
        ...current.filter((a) => a.organization_id !== org),
        {
          organization_id: org,
          paused: f.has("paused"),
          opens_at: String(f.get("opens")),
          closes_at: String(f.get("closes")),
          weekdays: f.getAll("days").map(Number),
          note: String(f.get("note") ?? ""),
        },
      ]);
      if (admin && sub) {
        const g = await client.rpc("set_subscription_grace", { org, days: Number(f.get("grace")) });
        if (g.error)
          throw new Error(
            `Availability saved; grace period could not be updated: ${g.error.message}`,
          );
        setSubscriptions((current) =>
          current.map((s) =>
            s.organization_id === org ? { ...s, grace_days: Number(f.get("grace")) } : s,
          ),
        );
      }
      setMessage("Provider settings saved.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="role-more-page provider-settings">
      <a className="more-back-link" href="/more">
        ← More
      </a>
      <Card className="management-card role-more-detail-card provider-settings__card">
        {admin || orgs.length > 1 ? (
          <label className="field">
            <span>Provider business</span>
            <select value={org} onChange={(e) => setOrg(e.target.value)}>
              <option value="">Choose a business</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
        ) : org ? (
          <h2>{orgs.find((o) => o.id === org)?.name}</h2>
        ) : (
          <p role="status">
            {loading
              ? "Loading your business settings…"
              : "No business settings are available for this account."}
          </p>
        )}
        {message && (
          <p className="form-message" role="status">
            {message}
          </p>
        )}
        {org ? (
          <form key={org} onSubmit={save} className="form-grid provider-settings__form">
            <label className="provider-settings__pause">
              <input name="paused" type="checkbox" defaultChecked={value?.paused} />
              <span>
                <strong>Temporarily pause new requests</strong>
                <small>Existing work can still finish while your business is paused.</small>
              </span>
            </label>
            <div className="provider-settings__section">
              <div className="provider-settings__heading">
                <h3>Opening hours</h3>
              </div>
              <div className="provider-settings__hours">
                <label className="field">
                  <span>Opens</span>
                  <input
                    type="time"
                    name="opens"
                    defaultValue={value?.opens_at ?? "00:00"}
                    aria-describedby="provider-hours-help"
                    required
                  />
                </label>
                <label className="field">
                  <span>Closes</span>
                  <input
                    type="time"
                    name="closes"
                    defaultValue={value?.closes_at ?? "00:00"}
                    aria-describedby="provider-hours-help"
                    required
                  />
                </label>
              </div>
              <p id="provider-hours-help">
                Myanmar time · Use the same opening and closing time for 24-hour service.
              </p>
              <fieldset className="provider-settings__days">
                <legend>Open days</legend>
                <div>
                  {[
                    "Sunday",
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                  ].map((d, i) => (
                    <label key={d}>
                      <input
                        type="checkbox"
                        name="days"
                        value={i}
                        defaultChecked={value?.weekdays.includes(i) ?? true}
                      />
                      <span>{d}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="field">
                <span>Availability note</span>
                <textarea name="note" rows={3} maxLength={240} defaultValue={value?.note} />
              </label>
            </div>
            <section
              className="provider-settings__section"
              aria-labelledby="provider-subscription-title"
            >
              <h3 id="provider-subscription-title">Subscription</h3>
              <dl className="provider-settings__subscription">
                <div>
                  <dt>Status</dt>
                  <dd>{sub?.status ?? "Not configured"}</dd>
                </div>
                <div>
                  <dt>Renewal</dt>
                  <dd>{sub?.renews_at ?? "Contact admin"}</dd>
                </div>
                <div>
                  <dt>Grace period</dt>
                  <dd>{sub?.grace_days ?? 3} days</dd>
                </div>
              </dl>
              {sub?.renews_at && new Date(sub.renews_at).getTime() - now < 7 * 86400000 && (
                <p className="form-message" role="status">
                  Your renewal is due soon or overdue. Submit your payment reference to avoid
                  interruption.
                </p>
              )}
              {admin && sub && (
                <label className="field">
                  <span>Renewal grace days</span>
                  <input
                    type="number"
                    name="grace"
                    min={0}
                    max={30}
                    defaultValue={sub.grace_days}
                    required
                  />
                </label>
              )}
            </section>
            <div className="provider-settings__actions">
              <button className="button" type="submit" disabled={busy}>
                {busy ? "Saving…" : "Save availability"}
              </button>
            </div>
          </form>
        ) : null}
      </Card>
    </div>
  );
}
